import { formatDateTime } from './format';

/**
 * Stored data (a report's context, a device, a change's payload) as words a
 * person reads, never as JSON: "amountMinor: 500" becomes "Amount 5.00",
 * "osVersion" becomes "OS version", true becomes "Yes".
 */

export interface ReadablePair {
  label: string;
  value: string;
  /** An id or a code: shown in a fixed-width face, never reworded. */
  code: boolean;
}

/** Short words that are written in capitals. */
const CAPITALS = new Set(['id', 'os', 'ip', 'url', 'sms', 'otp', 'pin', 'kyc']);

const MINOR = /[ _]?minor$/i;
const ID_LIKE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CODE_LIKE = /^[A-Z0-9]+(?:_[A-Z0-9]+)+$/;
// Milliseconds since 1970, for any moment from 2001 to 2286.
const isEpochMs = (value: number) => value >= 1e12 && value < 1e13;

/** "amountMinor" -> "Amount", "os_version" -> "OS version", "ts" -> "Time". */
export function readableLabel(key: string): string {
  if (key === 'ts') return 'Time';
  const words = key
    .replace(MINOR, '')
    .replaceAll(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replaceAll(/[_.-]+/g, ' ')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((word) => (CAPITALS.has(word) ? word.toUpperCase() : word));
  const text = words.join(' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function readableNumber(key: string, value: number): string {
  if (MINOR.test(key)) {
    return (value / 100).toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  if (isEpochMs(value)) return formatDateTime(new Date(value).toISOString());
  return String(value);
}

/** One value as text. A list or a group inside it is written out in words. */
export function readableValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return readableNumber(key, value);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value
      .map((item) => readableValue(key, item))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'object') {
    return readablePairsOf(value)
      .map((pair) => `${pair.label}: ${pair.value}`)
      .join(', ');
  }
  return '';
}

/** A group's fields as labelled values, leaving out the empty ones. */
export function readablePairsOf(value: object): ReadablePair[] {
  return Object.entries(value).flatMap(([key, raw]) => {
    const text = readableValue(key, raw);
    if (!text) return [];
    const code =
      typeof raw === 'string' && (ID_LIKE.test(raw) || CODE_LIKE.test(raw));
    return [{ label: readableLabel(key), value: text, code }];
  });
}
